import React, { useState } from 'react';

interface Invoice {
  id: string;
  invoiceNumber: string;
  title: string;
  amount: string;
  dueDate: string;
}

interface PaymentModalProps {
  invoice: Invoice;
  onClose: () => void;
  onSuccess: () => void;
}

export default function PaymentModal({ invoice, onClose, onSuccess }: PaymentModalProps) {
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardholderName: '',
    billingAddress: {
      line1: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US'
    }
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [step, setStep] = useState<'details' | 'processing' | 'success'>('details');

  const formatCardNumber = (value: string) => {
    // Remove all non-digits
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    // Add spaces every 4 digits
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    if (formatted.replace(/\s/g, '').length <= 16) {
      setPaymentData(prev => ({ ...prev, cardNumber: formatted }));
    }
  };

  const validateForm = () => {
    const newErrors: string[] = [];
    
    if (!paymentData.cardNumber || paymentData.cardNumber.replace(/\s/g, '').length < 13) {
      newErrors.push('Please enter a valid card number');
    }
    
    if (!paymentData.expiryMonth || !paymentData.expiryYear) {
      newErrors.push('Please enter card expiry date');
    }
    
    if (!paymentData.cvv || paymentData.cvv.length < 3) {
      newErrors.push('Please enter a valid CVV');
    }
    
    if (!paymentData.cardholderName.trim()) {
      newErrors.push('Please enter cardholder name');
    }
    
    if (!paymentData.billingAddress.line1.trim()) {
      newErrors.push('Please enter billing address');
    }
    
    if (!paymentData.billingAddress.city.trim()) {
      newErrors.push('Please enter billing city');
    }
    
    if (!paymentData.billingAddress.state.trim()) {
      newErrors.push('Please enter billing state');
    }
    
    if (!paymentData.billingAddress.postalCode.trim()) {
      newErrors.push('Please enter billing postal code');
    }

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    setErrors([]);
    setStep('processing');

    try {
      const token = localStorage.getItem('authToken');
      
      // Step 1: Create payment intent
      const intentResponse = await fetch('/api/payments/create-intent', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ invoiceId: parseInt(invoice.id) })
      });

      const intentData = await intentResponse.json();
      
      if (!intentData.success) {
        throw new Error(intentData.message || 'Failed to create payment intent');
      }

      // Simulate payment processing (in real implementation, this would use Stripe Elements)
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Step 2: Simulate successful payment and confirm
      const confirmResponse = await fetch('/api/payments/confirm', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          paymentIntentId: intentData.data.paymentIntentId,
          invoiceId: parseInt(invoice.id)
        })
      });

      const confirmData = await confirmResponse.json();
      
      if (confirmData.success) {
        setStep('success');
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        throw new Error(confirmData.message || 'Payment confirmation failed');
      }

    } catch (error) {
      console.error('Payment error:', error);
      setErrors([error instanceof Error ? error.message : 'Payment failed. Please try again.']);
      setStep('details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: string) => {
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (step === 'processing') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-md w-full p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ggaa-green mx-auto mb-4"></div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Processing Payment</h3>
          <p className="text-gray-600">Please wait while we process your payment...</p>
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">Do not close this window or refresh the page.</p>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Payment Successful!</h3>
          <p className="text-gray-600 mb-4">Your payment of {formatCurrency(invoice.amount)} has been processed successfully.</p>
          <div className="text-sm text-gray-500">
            <p>Invoice: {invoice.invoiceNumber}</p>
            <p>Redirecting you back...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h3 className="text-2xl font-semibold text-gray-900">💳 Pay Invoice</h3>
                <p className="text-gray-600 mt-1">Secure payment for {invoice.invoiceNumber}</p>
              </div>
              <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl">✕</button>
            </div>

            {/* Invoice Summary */}
            <div className="mb-6 p-4 bg-ggaa-cream rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Invoice Summary</h4>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">{invoice.title}</p>
                  <p className="text-xs text-gray-500">Due: {formatDate(invoice.dueDate)}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-ggaa-green">{formatCurrency(invoice.amount)}</p>
                </div>
              </div>
            </div>

            {errors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <ul className="list-disc list-inside text-red-700 text-sm">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Card Information */}
              <div className="md:col-span-2">
                <h4 className="font-medium text-gray-900 mb-4">💳 Card Information</h4>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Card Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={paymentData.cardNumber}
                  onChange={handleCardNumberChange}
                  placeholder="1234 5678 9012 3456"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiry Month <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={paymentData.expiryMonth}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, expiryMonth: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                >
                  <option value="">Month</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={month.toString().padStart(2, '0')}>
                      {month.toString().padStart(2, '0')} - {new Date(0, month - 1).toLocaleString('en', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Expiry Year <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  value={paymentData.expiryYear}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, expiryYear: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                >
                  <option value="">Year</option>
                  {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() + i).map(year => (
                    <option key={year} value={year.toString()}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  CVV <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  maxLength={4}
                  value={paymentData.cvv}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, cvv: e.target.value.replace(/\D/g, '') }))}
                  placeholder="123"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cardholder Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={paymentData.cardholderName}
                  onChange={(e) => setPaymentData(prev => ({ ...prev, cardholderName: e.target.value }))}
                  placeholder="John Doe"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                />
              </div>

              {/* Billing Address */}
              <div className="md:col-span-2 mt-4">
                <h4 className="font-medium text-gray-900 mb-4">🏠 Billing Address</h4>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address Line 1 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={paymentData.billingAddress.line1}
                  onChange={(e) => setPaymentData(prev => ({ 
                    ...prev, 
                    billingAddress: { ...prev.billingAddress, line1: e.target.value }
                  }))}
                  placeholder="123 Main Street"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={paymentData.billingAddress.city}
                  onChange={(e) => setPaymentData(prev => ({ 
                    ...prev, 
                    billingAddress: { ...prev.billingAddress, city: e.target.value }
                  }))}
                  placeholder="Nashville"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={paymentData.billingAddress.state}
                  onChange={(e) => setPaymentData(prev => ({ 
                    ...prev, 
                    billingAddress: { ...prev.billingAddress, state: e.target.value }
                  }))}
                  placeholder="TN"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Postal Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={paymentData.billingAddress.postalCode}
                  onChange={(e) => setPaymentData(prev => ({ 
                    ...prev, 
                    billingAddress: { ...prev.billingAddress, postalCode: e.target.value }
                  }))}
                  placeholder="37201"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                <select
                  value={paymentData.billingAddress.country}
                  onChange={(e) => setPaymentData(prev => ({ 
                    ...prev, 
                    billingAddress: { ...prev.billingAddress, country: e.target.value }
                  }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-ggaa-green"
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                </select>
              </div>
            </div>

            {/* Security Notice */}
            <div className="mt-6 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-9a2 2 0 00-2-2H6a2 2 0 00-2 2v9a2 2 0 002 2zm10-12V7a4 4 0 00-8 0v4h8z"></path>
                </svg>
                <p className="text-sm text-green-700">
                  🔒 Your payment information is secure and encrypted
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-3 bg-ggaa-green text-white rounded-lg hover:bg-ggaa-green/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-lg"
              >
                {loading ? 'Processing...' : `💳 Pay ${formatCurrency(invoice.amount)}`}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
} 